import React from 'react';
import { Shield, Database, UserCheck, Mail, Eye, Lock } from 'lucide-react';

export const Privacy: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center space-x-3 mb-4">
            <Shield className="w-8 h-8 text-blue-600" />
            <h1 className="text-3xl font-bold text-gray-900">Politique de Confidentialité</h1>
          </div>
          <p className="text-gray-600">
            Dernière mise à jour : {new Date().toLocaleDateString('fr-FR')}
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-lg shadow-sm p-8 space-y-8">
          
          {/* Introduction */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Introduction</h2>
            <p className="text-gray-700 leading-relaxed">
              DPEWizard s'engage à protéger la confidentialité de vos données personnelles. Cette politique 
              de confidentialité explique comment nous collectons, utilisons, stockons et protégeons vos 
              informations lorsque vous utilisez notre plateforme d'analyse DPE.
            </p>
          </section>

          {/* Données collectées */}
          <section>
            <div className="flex items-center space-x-3 mb-4">
              <Database className="w-6 h-6 text-blue-600" />
              <h2 className="text-2xl font-semibold text-gray-900">Données que nous collectons</h2>
            </div>
            
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">Informations d'inscription</h3>
                <ul className="list-disc list-inside text-gray-700 space-y-1 ml-4">
                  <li>Adresse email</li>
                  <li>Mot de passe (crypté)</li>
                  <li>Date d'inscription</li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">Données DPE analysées</h3>
                <ul className="list-disc list-inside text-gray-700 space-y-1 ml-4">
                  <li>Fichiers DPE téléchargés</li>
                  <li>Résultats d'analyse automatique</li>
                  <li>Réponses aux questionnaires contextuels</li>
                  <li>Recommandations générées</li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">Données techniques</h3>
                <ul className="list-disc list-inside text-gray-700 space-y-1 ml-4">
                  <li>Adresse IP</li>
                  <li>Type de navigateur et version</li>
                  <li>Pages visitées et temps de session</li>
                  <li>Logs d'erreurs et de performance</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Utilisation des données */}
          <section>
            <div className="flex items-center space-x-3 mb-4">
              <UserCheck className="w-6 h-6 text-blue-600" />
              <h2 className="text-2xl font-semibold text-gray-900">Utilisation de vos données</h2>
            </div>
            
            <p className="text-gray-700 mb-4">Nous utilisons vos données personnelles pour :</p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Fournir et améliorer nos services d'analyse DPE</li>
              <li>Personnaliser votre expérience sur la plateforme</li>
              <li>Vous envoyer des notifications importantes sur votre compte</li>
              <li>Assurer la sécurité et la maintenance de nos services</li>
              <li>Respecter nos obligations légales</li>
              <li>Analyser l'utilisation de la plateforme pour l'améliorer</li>
            </ul>
          </section>

          {/* Partage des données */}
          <section>
            <div className="flex items-center space-x-3 mb-4">
              <Eye className="w-6 h-6 text-blue-600" />
              <h2 className="text-2xl font-semibold text-gray-900">Partage de vos données</h2>
            </div>
            
            <p className="text-gray-700 mb-4">
              Nous ne vendons, ne louons ni ne partageons vos données personnelles avec des tiers, 
              sauf dans les cas suivants :
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Avec votre consentement explicite</li>
              <li>Pour se conformer à une obligation légale</li>
              <li>Avec nos prestataires de services (hébergement, support technique) sous contrat de confidentialité</li>
              <li>En cas de fusion, acquisition ou vente d'actifs de l'entreprise</li>
            </ul>
          </section>

          {/* Sécurité */}
          <section>
            <div className="flex items-center space-x-3 mb-4">
              <Lock className="w-6 h-6 text-blue-600" />
              <h2 className="text-2xl font-semibold text-gray-900">Sécurité des données</h2>
            </div>
            
            <p className="text-gray-700 mb-4">
              Nous mettons en place des mesures de sécurité techniques et organisationnelles appropriées 
              pour protéger vos données :
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Chiffrement des données sensibles (mots de passe, communications)</li>
              <li>Accès restreint aux données personnelles</li>
              <li>Surveillance continue de notre infrastructure</li>
              <li>Sauvegardes régulières et sécurisées</li>
              <li>Formation de notre équipe aux bonnes pratiques de sécurité</li>
            </ul>
          </section>

          {/* Droits RGPD */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Vos droits</h2>
            
            <p className="text-gray-700 mb-4">
              Conformément au RGPD, vous disposez des droits suivants :
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li><strong>Droit d'accès :</strong> consulter les données que nous détenons sur vous</li>
              <li><strong>Droit de rectification :</strong> corriger des données inexactes</li>
              <li><strong>Droit à l'effacement :</strong> demander la suppression de vos données</li>
              <li><strong>Droit à la portabilité :</strong> récupérer vos données dans un format standard</li>
              <li><strong>Droit d'opposition :</strong> vous opposer au traitement de vos données</li>
              <li><strong>Droit de limitation :</strong> limiter l'utilisation de vos données</li>
            </ul>

            <p className="text-gray-700 mt-4">
              Pour exercer ces droits, contactez-nous à{' '}
              <a href="mailto:support@dpewizard.com" className="text-blue-600 hover:underline">
                support@dpewizard.com
              </a>
            </p>
          </section>

          {/* Conservation */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Conservation des données</h2>
            
            <p className="text-gray-700 mb-4">Nous conservons vos données personnelles :</p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Tant que votre compte est actif</li>
              <li>Pendant 3 ans après la fermeture de votre compte (obligations légales)</li>
              <li>Les données DPE analysées sont supprimées après 1 an d'inactivité</li>
              <li>Les logs techniques sont conservés 6 mois maximum</li>
            </ul>
          </section>

          {/* Cookies */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Cookies et technologies similaires</h2>
            
            <p className="text-gray-700 mb-4">
              Nous utilisons des cookies essentiels au fonctionnement du site :
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Cookies de session pour maintenir votre connexion</li>
              <li>Cookies de préférences pour sauvegarder vos paramètres</li>
              <li>Cookies de sécurité pour protéger votre compte</li>
            </ul>
            
            <p className="text-gray-700 mt-4">
              Vous pouvez désactiver les cookies dans votre navigateur, mais certaines fonctionnalités 
              pourraient ne plus fonctionner correctement.
            </p>
          </section>

          {/* Contact */}
          <section>
            <div className="flex items-center space-x-3 mb-4">
              <Mail className="w-6 h-6 text-blue-600" />
              <h2 className="text-2xl font-semibold text-gray-900">Contact</h2>
            </div>
            
            <p className="text-gray-700">
              Pour toute question concernant cette politique de confidentialité ou l'utilisation 
              de vos données personnelles, contactez-nous :
            </p>
            
            <div className="mt-4 p-4 bg-gray-50 rounded-lg">
              <p className="text-gray-700">
                <strong>Email :</strong>{' '}
                <a href="mailto:support@dpewizard.com" className="text-blue-600 hover:underline">
                  support@dpewizard.com
                </a>
              </p>
              <p className="text-gray-700 mt-2">
                <strong>Responsable du traitement :</strong> DPEWizard
              </p>
            </div>
          </section>

          {/* Modifications */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Modifications de cette politique</h2>
            
            <p className="text-gray-700">
              Nous nous réservons le droit de modifier cette politique de confidentialité à tout moment. 
              En cas de modification substantielle, nous vous en informerons par email ou via une 
              notification sur la plateforme. La version actualisée sera toujours disponible sur cette page.
            </p>
          </section>

        </div>
      </div>
    </div>
  );
};

export default Privacy;